//================================================================
//  Component: PTE RHF Lookup
//================================================================

//  Purpose: This is an input field that allows you to search 'oracleProjects' collection for PTOs

//  Properties:
//    - required = {boolean, this determines if this field is required}
//    - register = {react hooks forms state, connects the field to the form state}
//    - setValue = {react hooks form state, programmatically sets the value for a field}
//    - errors = {react hooks form state, stores the errors for the field}
//    - clearErrors = {react hooks form state, allows you to clear errors on a certain field}
//    - disabled = <Optional> {boolean, determines if the field is disabled}
//    - watch = {react hooks form state, allows you to watch a certain field}
//    - setError = {react hook form state, used to set error state}

//  Example:
//
//      <PTELookupRHF
//          required={false}
//          register={register}
//          setValue={setValue}
//          errors={errors}
//          clearErrors={clearErrors}
//          disabled={false}
//          watch={watch}
//          setError={setError}
//      ></PTELookupRHF> 

//  IMPORTANT: Standard fieldId's are returned as part of this component, these are:
//      
//     'projectcode', 'tasknumber' & 'expendituretype'
//

//================================================================


//Libraries
import React, { useReducer, useEffect } from 'react';

//Contexts

//Components

//Functions
import GetDocument from '../../Library/GetDocument';

//Images


export default function PTELookupRHF({
    required,
    register,
    setValue,
    errors,
    clearErrors,
    disabled,
    watch, 
    setError
}) {

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'searchInput': '',
            'queryMessage': '',
            'taskNumbers': [],
            'projectcodename': '',
            'inputDisabled': disabled
        }
    );

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Onload > Clear errors on all three fields
    useEffect(() => {

        if (clearErrors === undefined) return;

        clearErrors('projectcode');
        clearErrors('tasknumber');
        clearErrors('expendituretype');

    }, [clearErrors]);

    // Watch the value of projectcode and trigger handleSearch
    const projectCode = watch('projectcode'); // Use watch to observe changes in projectcode

    useEffect(() => {

        if (projectCode) {
            handleSearch(projectCode);
        }
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectCode]);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to handle search
    const handleSearch = (value) => {

        if (value.length < 6) return setSearchData({ 'taskNumbers': [] }); // perform search AFTER 6 characters

        setSearchData({ 'inputDisabled': true }); // disable input, allow fetching of data...

        // Search by characters in email
        GetDocument('oracleProjects', value, true).then((doc) => {

            // No PTE found
            if (doc === undefined) {

                setError('projectcode', {
                    type: 'required',
                    message: 'This project code does not exist. Please enter a valid project code.'
                });
                setSearchData({
                    'taskNumbers': [],
                    'inputDisabled': false,
                });
                setValue('tasknumber', '');
                setValue('expendituretype', '');
                setValue('projectcodename', '');

                return;

            }

            clearErrors('projectcode');

            const taskNumbers = Object.keys(doc.taskNumbers); // taskNumbers are arrays?!

            // Sort to decending order
            taskNumbers.sort();

            // Found PTE
            setSearchData({
                'taskNumbers': taskNumbers,
                'inputDisabled': false,
                'projectcodename': doc?.projectName,
            });
            setValue('projectcodename', doc?.projectName);

        }).catch((error) => {

            console.log(error);

            setSearchData({
                'taskNumbers': [],
                'inputDisabled': false,
            });
            setValue('tasknumber', '');
            setValue('expendituretype', '');
            setValue('projectcodename', '');

        });

    }

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col w-full h-full relative gap-2 md:w-[600px]'>

            {/* ------------------------------------------------------ */}
            {/*  Project Code - projectcode                            */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row justify-between'>

                <label className='w-[130px]'>Project Code</label>

                <input
                    className={errors.projectcode ? ('Input-Field-Text-Error w-36 md:w-[400px]') : ('Input-Field-Text w-36 md:w-[400px]')}
                    type='text'
                    placeholder={'Project Code'}
                    {...register('projectcode', {
                        required: required ? 'These fields are required' : false,
                    })}
                    onChange={(e) => {
                        setValue('projectcode', e.target.value);
                    }}
                    autoComplete='off'
                    disabled={searchData.inputDisabled}
                ></input>

            </div>

            {/* ------------------------------------------------------ */}
            {/*  Project Name - projectcodename                        */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row justify-between' hidden={watch('projectcode') === '' || watch('projectcode') === undefined || watch('projectcode')?.length < 6 || errors.projectcode}>

                <label className='w-[130px]'>Project Name</label>

                <input
                    className={errors.projectcodename ? ('Input-Field-Text-Error w-36 md:w-[400px]') : ('Input-Field-Text w-36 md:w-[400px]')}
                    type='text'
                    placeholder={'Project Name'}
                    {...register('projectcodename')}
                    value={searchData?.projectcodename}
                    disabled
                ></input>

            </div>

            {/* ------------------------------------------------------ */}
            {/*  Task Number - tasknumber                              */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row justify-between'>

                <label className='w-[130px]'>Task Number</label>

                {
                    searchData.taskNumbers.length > 0 ?

                        // Found PTE doc --> Show task number options
                        <select
                            className={errors.tasknumber ? ('Input-Field-Select-Error w-36 md:w-[400px]') : ('Input-Field-Select w-36 md:w-[400px]')}
                            {...register('tasknumber', {
                                required: required ? 'These fields are required' : false,
                            })}
                            onChange={() => {

                                if (searchData.taskNumbers.length === 0) return;

                                setValue('expendituretype', 'ICT Costs');

                            }}
                        >
                            <option hidden value=''>Select one</option>
                            {
                                searchData.taskNumbers.map((option, index) => (
                                    <option key={index} value={option}>{option}</option>
                                ))
                            }

                        </select>

                        :

                        // No PTE doc found --> allow free text
                        <input
                            className={errors.tasknumber ? ('Input-Field-Text-Error w-36 md:w-[400px]') : ('Input-Field-Text w-36 md:w-[400px]')}
                            type='text'
                            placeholder={'Task Number'}
                            {...register('tasknumber', {
                                required: required ? 'These fields are required' : false,
                            })}
                            autoComplete='off'
                            disabled={searchData.inputDisabled}
                        ></input>

                }

            </div>

            {/* ------------------------------------------------------ */}
            {/*  ICT Costs - expendituretype                           */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row justify-between'>

                <label className='w-[130px]'>Expenditure Type</label>

                <input
                    className={errors.expendituretype ? ('Input-Field-Text-Error w-36 md:w-[400px]') : ('Input-Field-Text w-36 md:w-[400px]')}
                    type='text'
                    placeholder={'ICT Costs'}
                    {...register('expendituretype', {
                        required: required ? 'These fields are required' : false,
                    })}
                    autoComplete='off'
                    disabled={searchData.inputDisabled}
                ></input>


            </div>

            {/* Error Message */}
            {errors.projectcode && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.projectcode.message}</p>}

        </div>
    )

}
